var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { ArrayInputContext, FieldTitle, InputHelperText, Labeled, LinearProgress, isRequired, ResourceContextProvider, useRecordContext, useResourceContext, useTranslate, } from 'react-admin';
import clsx from 'clsx';
import { InputLabel, FormControl, FormHelperText, Typography, styled, } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useReferenceManyInputController, } from './useReferenceManyInputController';
/**
 * Use `<ReferenceManyInput>` in an `<Edit>` or `<Create>` view to edit one-to-many relationships,
 * e.g. to edit the variants of a product in the product edition view.
 *
 * @example
 * import {
 *     Edit,
 *     SimpleForm,
 *     TextInput,
 *     NumberInput,
 *     ReferenceInput,
 *     SelectInput,
 * } from 'react-admin';
 * import { ReferenceManyInput } from '@react-admin/ra-relationships';
 *
 * const ProductEdit = () => (
 *     <Edit mutationMode="optimistic">
 *         <SimpleForm>
 *             <TextInput source="name" />
 *             <NumberInput source="price" />
 *             <ReferenceInput source="category_id" reference="categories" />
 *             <ReferenceManyInput reference="variants" target="product_id">
 *                 <SimpleFormIterator inline>
 *                     <TextInput source="sku" />
 *                     <SelectInput source="size" choices={sizes} />
 *                     <SelectInput source="color" choices={colors} />
 *                     <NumberInput source="stock" defaultValue={0} />
 *                 </SimpleFormIterator>
 *             </ReferenceManyInput>
 *         </SimpleForm>
 *     </Edit>
 * );
 */
export var ReferenceManyInput = function (_a) {
    var _b;
    var margin = _a.margin, variant = _a.variant, disabled = _a.disabled, children = _a.children, className = _a.className, helperText = _a.helperText, label = _a.label, sx = _a.sx, props = __rest(_a, ["margin", "variant", "disabled", "children", "className", "helperText", "label", "sx"]);
    var record = useRecordContext(props);
    var resource = useResourceContext(props);
    var _c = useReferenceManyInputController(props), fieldArray = _c.fieldArray, fetchError = _c.error, isLoading = _c.isLoading, temporaryFieldName = _c.temporaryFieldName;
    var _d = useFormContext(), getFieldState = _d.getFieldState, formState = _d.formState;
    var _e = getFieldState(temporaryFieldName, formState), isDirty = _e.isDirty, invalid = _e.invalid, error = _e.error;
    var translate = useTranslate();
    if (isLoading) {
        return (React.createElement(Root, { fullWidth: true, margin: margin, sx: sx, "data-testid": "loading" },
            React.createElement(Labeled, { label: label, source: props.reference, resource: resource },
                React.createElement(LinearProgress, null))));
    }
    if (fetchError) {
        return (React.createElement(Root, { fullWidth: true, margin: margin, sx: sx, "data-testid": "error" },
            React.createElement(Labeled, { label: label, source: props.reference, resource: resource },
                React.createElement(Typography, { sx: { color: 'error.main' } }, translate('ra-relationships.referenceManyInput.fetchError', {
                    _: 'Error fetching references',
                })))));
    }
    return (React.createElement(ResourceContextProvider, { value: props.reference },
        React.createElement(Root, { fullWidth: true, margin: margin, className: clsx('ra-input', "ra-input-" + temporaryFieldName, className), error: (isDirty || formState.isSubmitted) && invalid, sx: sx },
            React.createElement(InputLabel, { htmlFor: temporaryFieldName, className: ReferenceManyInputClasses.label, shrink: true, error: (isDirty || formState.isSubmitted) && invalid },
                React.createElement(FieldTitle, { label: label, source: temporaryFieldName, resource: props.reference, isRequired: isRequired(props.validate) })),
            React.createElement(ArrayInputContext.Provider, { value: fieldArray }, React.cloneElement(React.Children.only(children), __assign(__assign({}, fieldArray), { record: record, resource: props.reference, source: temporaryFieldName, variant: variant, margin: margin, disabled: disabled, disableReordering: true }))),
            !!((isDirty || formState.isSubmitted) && !!error) ||
                helperText ? (React.createElement(FormHelperText, { error: (isDirty || formState.isSubmitted) && !!error },
                React.createElement(InputHelperText, { touched: isDirty || formState.isSubmitted, error: (_b = error === null || error === void 0 ? void 0 : error.root) === null || _b === void 0 ? void 0 : _b.message, helperText: helperText }))) : null)));
};
var PREFIX = 'RaReferenceManyInput';
export var ReferenceManyInputClasses = {
    label: PREFIX + "-label",
};
var Root = styled(FormControl, {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        _b["& ." + ReferenceManyInputClasses.label] = {
            top: theme.spacing(-0.5),
            left: theme.spacing(-1.5),
        },
        _b);
});
